import { Grid, Stack } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { SectionTitle } from "./SectionTitle";
import { ControlledTextField } from "../forms/ControlledTextField";
import { ControlledCountryPhoneField } from "../forms/ControlledCountryPhoneField";
import { ControlledSelect } from "../forms/ControlledSelect";
import { CheckoutFormTypeFields, useCheckoutForm } from "./hooks/useCheckoutForm";
import { DOC_DNI_ID, OrderPersonalInformationFields } from "../../types/purchaseOrder/Order";
import { CHECKOUT_MAXLEN } from "../../utils/checkoutFieldMaxLengths";
import { textFieldStyle } from "./CouponStyles";
import { HttpCache } from "../../http/httpCache";
import { EntityWithIdAndDescription } from "../../types/baseEntities";
import type { CheckoutFormType } from "./hooks/useCheckoutForm";
import type { Path } from "react-hook-form";

export const PersonalInformationSection: React.FC = () => {
    const { methods } = useCheckoutForm();
    const baseName = CheckoutFormTypeFields.PersonalInformation;

    const docTipoName =
        `${baseName}.${OrderPersonalInformationFields.DocumentoTipo}` as Path<CheckoutFormType>;
    const countryCodeName =
        `${baseName}.${OrderPersonalInformationFields.AreaCode}` as Path<CheckoutFormType>;
    const phoneName =
        `${baseName}.${OrderPersonalInformationFields.PhoneNumber}` as Path<CheckoutFormType>;
    const razonSocialName =
        `${baseName}.${OrderPersonalInformationFields.RazonSocial}` as Path<CheckoutFormType>;
    const afipTipoName =
        `${baseName}.${OrderPersonalInformationFields.AfipResponsableTipo}` as Path<CheckoutFormType>;

    const [documentoTipos, setDocumentoTipos] = useState<EntityWithIdAndDescription[]>([]);
    const [afipResponsableTipos, setAfipResponsableTipos] = useState<EntityWithIdAndDescription[]>(
        [],
    );

    useEffect(() => {
        HttpCache.getIdentityDocumentTypes()
            .then((tipos) => {
                setDocumentoTipos(tipos);
                const current = methods.getValues(docTipoName);
                if (current == null && tipos.length > 0) {
                    methods.setValue(docTipoName, tipos[0].id as any, { shouldValidate: false });
                }
            })
            .catch(() => {});
        HttpCache.getAfipResponsableTipos()
            .then(setAfipResponsableTipos)
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const silentTrigger = useCallback(
        () => Promise.resolve(true) as ReturnType<typeof methods.trigger>,
        [],
    );

    const docTipo = methods.watch(docTipoName) as number | null | undefined;
    const esDNI = docTipo === DOC_DNI_ID;

    const firstName = methods.watch(
        `${baseName}.${OrderPersonalInformationFields.FirstName}` as Path<CheckoutFormType>,
    );
    const lastName = methods.watch(
        `${baseName}.${OrderPersonalInformationFields.LastName}` as Path<CheckoutFormType>,
    );

    useEffect(() => {
        if (!esDNI) return;
        const generated = [lastName, firstName].filter(Boolean).join(", ");
        if (generated) {
            methods.setValue(razonSocialName, generated as any, { shouldValidate: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [firstName, lastName, esDNI]);

    return (
        <Stack mb={2}>
            <SectionTitle>Datos Personales</SectionTitle>

            <Grid container spacing={2}>
                {esDNI ? (
                    <>
                        <Grid size={{ xs: 12, sm: 2 }}>
                            <ControlledSelect
                                control={methods.control}
                                name={docTipoName}
                                label="Tipo"
                                options={documentoTipos}
                                sx={textFieldStyle}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 2 }}>
                            <ControlledTextField
                                control={methods.control}
                                label="Nro Documento"
                                name={`${baseName}.${OrderPersonalInformationFields.Document}`}
                                inputProps={{
                                    maxLength: CHECKOUT_MAXLEN.vchDocumento,
                                    inputMode: "numeric",
                                    pattern: "[0-9]*",
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <ControlledTextField
                                control={methods.control}
                                label="Nombre"
                                name={`${baseName}.${OrderPersonalInformationFields.FirstName}`}
                                required
                                inputProps={{ maxLength: CHECKOUT_MAXLEN.vchNombre }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <ControlledTextField
                                control={methods.control}
                                label="Apellido"
                                name={`${baseName}.${OrderPersonalInformationFields.LastName}`}
                                required
                                inputProps={{ maxLength: CHECKOUT_MAXLEN.vchApellido }}
                            />
                        </Grid>
                    </>
                ) : (
                    <>
                        <Grid size={{ xs: 12, sm: 2 }}>
                            <ControlledSelect
                                control={methods.control}
                                name={docTipoName}
                                label="Tipo"
                                options={documentoTipos}
                                sx={textFieldStyle}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 2 }}>
                            <ControlledTextField
                                control={methods.control}
                                label="CUIT / CUIL"
                                name={`${baseName}.${OrderPersonalInformationFields.Cuit}`}
                                required={docTipo != null}
                                inputProps={{
                                    maxLength: CHECKOUT_MAXLEN.vchCuit,
                                    inputMode: "numeric",
                                    pattern: "[0-9]*",
                                }}
                            />
                        </Grid>
                        {docTipo != null && (
                            <>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <ControlledTextField
                                        control={methods.control}
                                        label="Razón social"
                                        name={razonSocialName}
                                        required
                                        inputProps={{ maxLength: CHECKOUT_MAXLEN.vchRazonSocial }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <ControlledSelect
                                        control={methods.control}
                                        name={afipTipoName}
                                        label="Condición frente al IVA"
                                        options={afipResponsableTipos}
                                        sx={textFieldStyle}
                                    />
                                </Grid>
                            </>
                        )}
                    </>
                )}

                <Grid size={12}>
                    <ControlledCountryPhoneField<CheckoutFormType>
                        control={methods.control}
                        getValues={methods.getValues}
                        setValue={methods.setValue}
                        trigger={silentTrigger}
                        countryCodeName={countryCodeName}
                        phoneName={phoneName}
                        required
                        phoneMaxLength={CHECKOUT_MAXLEN.vchTelefono}
                        defaultCountryIso2="AR"
                        phoneHelperText='Ingresá código de área y número. Ej AR celular: " 9 11 1234 5678".'
                    />
                </Grid>
            </Grid>
        </Stack>
    );
};
